import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import * as PIXI from 'pixi.js';
import { useEffect, useState } from 'react';
import { shallowEqual } from 'react-redux';
import useMountedState from 'react-use/lib/useMountedState';
import { HAND_START_TABLE_ANIMATION_SET } from '../../../../animations/animationDefinitions';
import { TABLE_RAIL_IMAGE_URL, TABLE_SHADOW_IMAGE_URL } from '../../../../assets/themeTableAssetUrls';
import { getConfigValue } from '../../../../config/configValues';
import { useAppSelector } from '../../../../redux/store';
import { ALL_THEME_TABLE_PROPERTIES } from '../../../../types/themeTableTypes';
import { delay } from '../../../../utils/utils';
import { AnimationContainer } from '../../pixiComponents/animationContainer';
import { SolidColorPokerTableDisplay } from './solidColorPokerTableDisplay';
import { TexturedPokerTableDisplay } from './texturedPokerTableDisplay';
const RAIL_WIDTH_RATIO = 1.08;
const MAX_RAIL_THICKNESS_PX = 30;
export const PokerTableDisplay = () => {
    const isMounted = useMountedState();
    const tableAreaDimensions = useAppSelector((state) => state.gameDimensions.tableAreaDimensions, shallowEqual);
    const themeTableType = useAppSelector((state) => state.theme.themeTable?.type);
    const startHandTimestampMs = useAppSelector((state) => state.poker.startHandTimestampMs);
    const [feltTexture, setFeltTexture] = useState();
    const [railTexture, setRailTexture] = useState();
    const [shadowTexture, setShadowTexture] = useState();
    useEffect(() => {
        if (!themeTableType) {
            return;
        }
        const { imageUrl: feltImageUrl } = ALL_THEME_TABLE_PROPERTIES[themeTableType];
        const delayMs = getConfigValue('tableGraphicsDelay');
        if (delayMs) {
            // If `tableGraphicsDelay` is set, we are testing texture pop-in, so immediately clear textures when table is changed (not just on initial load).
            // Otherwise, the pop-in is only seen on initial load.
            setFeltTexture(undefined);
            setRailTexture(undefined);
        }
        const promise = delayMs ? delay(delayMs) : Promise.resolve();
        promise.then(() => {
            PIXI.Texture.fromURL(feltImageUrl).then((newTexture) => {
                if (isMounted()) {
                    setFeltTexture(newTexture);
                }
            });
            PIXI.Texture.fromURL(TABLE_RAIL_IMAGE_URL).then((newRailTexture) => {
                if (isMounted()) {
                    setRailTexture(newRailTexture);
                }
            });
            PIXI.Texture.fromURL(TABLE_SHADOW_IMAGE_URL).then((newShadowTexture) => {
                if (isMounted()) {
                    setShadowTexture(newShadowTexture);
                }
            });
        });
    }, [isMounted, themeTableType]);
    if (!tableAreaDimensions) {
        return null;
    }
    const feltDimensions = { w: tableAreaDimensions.w * 0.7, h: tableAreaDimensions.h * 0.6 };
    const minFeltWidth = Math.min(feltDimensions.w, feltDimensions.h);
    const railThicknessPx = Math.min(MAX_RAIL_THICKNESS_PX, minFeltWidth * RAIL_WIDTH_RATIO - minFeltWidth);
    const railDimensions = { w: feltDimensions.w + 2 * railThicknessPx, h: feltDimensions.h + 2 * railThicknessPx };
    if (!feltTexture || !railTexture || !shadowTexture) {
        return (_jsx(SolidColorPokerTableDisplay, { tableAreaDimensions: tableAreaDimensions, feltDimensions: feltDimensions, railDimensions: railDimensions }));
    }
    const contents = (_jsx(TexturedPokerTableDisplay, { tableAreaDimensions: tableAreaDimensions, feltDimensions: feltDimensions, railDimensions: railDimensions, feltTexture: feltTexture, railTexture: railTexture, shadowTexture: shadowTexture }));
    if (startHandTimestampMs === undefined) {
        return contents;
    }
    return (_jsx(AnimationContainer, { startTimestampMs: startHandTimestampMs, animationSet: HAND_START_TABLE_ANIMATION_SET, children: contents }));
};
